/**
 * One rule for "does this sales document talk about VAT at all".
 *
 * The operator's switch (Settings -> Invoices) says whether the company charges
 * VAT today. It cannot say whether a document written last year did, and that
 * is the whole difficulty: hiding the VAT column on an invoice that actually
 * carries VAT would print a subtotal and a total that do not add up, on a
 * document a client has already received as a PDF.
 *
 * So the switch decides what NEW work defaults to, and this rule decides what
 * is DRAWN: the column and the totals row appear whenever the company charges
 * VAT now, or whenever the document in front of the reader carries some. A
 * company that turns the switch off keeps every old invoice readable and only
 * stops printing an empty 0% column on the new ones.
 *
 * Four surfaces ask the question - the PDF, the public document page, the CRM's
 * own document screen and the client portal - so the answer lives here rather
 * than four times over.
 */

/** As much of a line as the rule needs. Anything with a VAT rate on it fits. */
export interface VatBearingLine {
  taxRatePct?: number | null;
}

/** Whether any line was priced with a rate. */
export function linesCarryVat(lines: readonly VatBearingLine[] | null | undefined): boolean {
  if (!lines) return false;
  return lines.some((li) => typeof li.taxRatePct === "number" && li.taxRatePct > 0);
}

export interface ShowVatInput {
  /** The company's switch: is it registered for VAT today. */
  vatEnabled: boolean;
  /** The VAT banked on the document, when the caller has the figure. */
  taxCents?: number | null;
  /** The document's lines, when the caller has them. */
  lines?: readonly VatBearingLine[] | null;
}

/**
 * Whether to draw the VAT column and the VAT totals row.
 *
 * `taxCents` and `lines` are both optional and both consulted: a totals-only
 * caller (the portal's list row) has the first, a lines-only caller (the CRM's
 * editor, mid-edit, before anything is banked) has the second, and the PDF has
 * both. A rate typed onto a line counts even before it rounds to a cent, so a
 * line at 24% on a zero-priced item still shows its column.
 */
export function showVatOn({ vatEnabled, taxCents, lines }: ShowVatInput): boolean {
  if (vatEnabled) return true;
  if (typeof taxCents === "number" && taxCents !== 0) return true;
  return linesCarryVat(lines);
}
